import React, { useCallback, useEffect, useRef } from 'react';
import type { DvcBboxIndex, DvcWeightIndex, OverlaySelection } from './types';
import { formatWeightLbs, setupThumbnailBboxOverlay } from './bboxOverlay';

export const SampleImageGrid: React.FC<{
  urls: string[];
  startIndex: number;
  onImageClick: (url: string) => void;
  onImageSelect: (url: string, globalIndex: number, event: React.MouseEvent) => void;
  selectedUrls: Set<string>;
  overlay: OverlaySelection;
  weightIndex: DvcWeightIndex | null;
  bboxIndex: DvcBboxIndex | null;
  classificationIndex: Record<string, string[]> | null;
  /** direct_s3: proxy URL built locally; presigned: map from S3 URL (null = batch still loading) */
  thumbnailDisplayMode: 'direct_s3' | 'presigned';
  apiBaseUrl: string;
  presignedThumbMap: Map<string, string> | null;
}> = ({
  urls,
  startIndex,
  onImageClick,
  onImageSelect,
  selectedUrls,
  overlay,
  weightIndex,
  bboxIndex,
  classificationIndex,
  thumbnailDisplayMode,
  apiBaseUrl,
  presignedThumbMap,
}) => {
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const imageRefs = useRef<Map<string, HTMLImageElement>>(new Map());

  const resolveThumb = (url: string): { href: string | null; loading: boolean } => {
    if (!url.startsWith('s3://')) {
      return { href: url, loading: false };
    }
    if (thumbnailDisplayMode === 'direct_s3') {
      const href = `${apiBaseUrl}/dvc/image/direct-s3?s3_url=${encodeURIComponent(url)}`;
      return { href, loading: false };
    }
    if (presignedThumbMap === null) {
      return { href: null, loading: true };
    }
    const p = presignedThumbMap.get(url);
    if (p) {
      return { href: p, loading: false };
    }
    return { href: null, loading: true };
  };

  const paintThumbBboxes = useCallback(
    (url: string, img?: HTMLImageElement | null) => {
      if (!overlay.bboxes) return;
      const image = img ?? imageRefs.current.get(url);
      const canvas = canvasRefs.current.get(url);
      if (!image || !canvas) return;
      if (!image.complete || image.naturalWidth === 0) return;
      const labels = bboxIndex?.[url] ?? [];
      setupThumbnailBboxOverlay(image, canvas, labels);
    },
    [overlay.bboxes, bboxIndex]
  );

  useEffect(() => {
    if (!overlay.bboxes) return;
    for (const url of urls) {
      paintThumbBboxes(url);
    }
  }, [overlay.bboxes, bboxIndex, urls, paintThumbBboxes]);

  return (
    <div className="dvc-sample-images-grid">
      {urls.map((url, index) => {
        const globalIndex = startIndex + index;
        const { href, loading } = resolveThumb(url);
        const hasError = url.startsWith('s3://') && !loading && !href;
        const isSelected = selectedUrls.has(url);
        const weight = overlay.weights && weightIndex ? weightIndex[url] ?? null : null;
        const showBboxes = overlay.bboxes;
        const classLabels = overlay.classifications
          ? (classificationIndex?.[url] ?? []).filter((name) => name.trim())
          : [];
        return (
          <div
            key={url}
            className={`dvc-sample-image-card ${isSelected ? 'dvc-sample-image-card-selected' : ''}`}
            onClick={(e) => onImageSelect(url, globalIndex, e)}
            onDoubleClick={
              overlay.classifications
                ? (e) => {
                    e.preventDefault();
                    onImageClick(url);
                  }
                : undefined
            }
            title={
              overlay.classifications
                ? 'Click to select for batch classification, Shift+click for range, Double-click to view'
                : 'Click to view image'
            }
          >
            <div className="dvc-sample-image-thumbnail">
              {loading ? (
                <div className="dvc-sample-image-loading">
                  <div>⏳</div>
                  <div>Loading...</div>
                </div>
              ) : hasError || !href ? (
                <div className="dvc-sample-image-error">
                  <div>❌</div>
                  <div>Failed to load</div>
                </div>
              ) : (
                <>
                  <img
                    src={href}
                    alt={`Sample ${globalIndex + 1}`}
                    onLoad={(e) => paintThumbBboxes(url, e.currentTarget)}
                    onError={(e) => {
                      console.error(`Image failed to load: ${href}`);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                    ref={(img) => {
                      if (img) {
                        imageRefs.current.set(url, img);
                        if (img.complete && img.naturalWidth > 0) {
                          paintThumbBboxes(url, img);
                        }
                      } else {
                        imageRefs.current.delete(url);
                      }
                    }}
                  />
                  {showBboxes && (
                    <canvas
                      className="dvc-bbox-canvas dvc-thumb-bbox-canvas"
                      ref={(el) => {
                        if (el) {
                          canvasRefs.current.set(url, el);
                          paintThumbBboxes(url);
                        } else {
                          canvasRefs.current.delete(url);
                        }
                      }}
                    />
                  )}
                </>
              )}
              {classLabels.length > 0 && (
                <div className="dvc-sample-image-class-badges">
                  {classLabels.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </div>
              )}
              {weight != null && (
                <div className="dvc-sample-image-weight-badge">{formatWeightLbs(weight)}</div>
              )}
            </div>
            <div className="dvc-sample-image-filename">{url.split('/').pop()}</div>
            <div className="dvc-sample-image-index">#{globalIndex + 1}</div>
          </div>
        );
      })}
    </div>
  );
};
